import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, SortState } from '../../components/SortableTh';
import { TableIconButton, TableIconLink } from '../../components/TableIconAction';
import {
  Badge,
  Button,
  Callout,
  EyeIcon,
  ListEmptyState,
  ListPagination,
  LoadingSpinner,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  Select,
  TextInput,
  TrashIcon,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { ExportIconButtons } from '../../components/ExportIconButtons';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerListItem } from '../../types/customer';
import { Office } from '../../types/office';
import { formatMoney } from '../../utils/format';
import {
  collectPages,
  exportTabularList,
  moneyCell,
  sumMoney,
  textCell,
  type ListExportFormat,
} from '../../utils/exportTabularList';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SERVER_SORT_KEYS = new Set([
  'name',
  'vatNumber',
  'email',
  'phone',
  'active',
  'accountingBooksCategory',
  'office',
  'assignedUser',
]);

function accountingBooksLabel(
  value: CustomerListItem['accountingBooksCategory'],
) {
  const labels = t().customers;
  if (value === 'B_SINGLE_ENTRY') return labels.booksSingleEntry;
  if (value === 'C_DOUBLE_ENTRY') return labels.booksDoubleEntry;
  return '—';
}

const customerSortGetters: Record<
  string,
  (item: CustomerListItem) => string | number | boolean | null | undefined
> = {
  name: (c) => c.name,
  vatNumber: (c) => c.vatNumber,
  phone: (c) => c.phone,
  email: (c) => c.email,
  agreedAmount: (c) => c.agreedAmount.total,
  outstandingBalance: (c) => c.outstandingBalance.total,
  active: (c) => c.active,
};

export function CustomerListPage() {
  const labels = t().customers;
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<CustomerListItem[]>([]);
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    pageCount: 1,
    totals: { agreedAmount: '0.00', outstandingBalance: '0.00' },
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(
    searchParams.get('search') ?? '',
  );
  const [offices, setOffices] = useState<Office[]>([]);
  const [assignees, setAssignees] = useState<
    Array<{ id: number; name: string; active: boolean }>
  >([]);

  const search = searchParams.get('search') ?? '';
  const active = searchParams.get('active') ?? '';
  const accountingBooksCategory =
    searchParams.get('accountingBooksCategory') ?? '';
  const officeId = searchParams.get('officeId') ?? '';
  const assignedUserId = searchParams.get('assignedUserId') ?? '';
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize =
    Number(searchParams.get('pageSize') ?? '20') || 20;
  const sortBy = searchParams.get('sortBy') ?? 'name';
  const sortDir =
    searchParams.get('sortDir') === 'desc' ? 'desc' : 'asc';
  const sort: SortState = { key: sortBy, direction: sortDir };

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    async function loadFilters() {
      try {
        const [officeList, assigneeList] = await Promise.all([
          api.getOffices(false),
          api.getCustomerAssignees(),
        ]);
        if (!cancelled) {
          setOffices(officeList);
          setAssignees(assigneeList);
        }
      } catch {
        if (!cancelled) {
          setOffices([]);
          setAssignees([]);
        }
      }
    }
    void loadFilters();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const serverSortBy = SERVER_SORT_KEYS.has(sortBy) ? sortBy : 'name';
        const result = await api.getCustomers({
          search: search || undefined,
          active: active || undefined,
          accountingBooksCategory: accountingBooksCategory || undefined,
          officeId: officeId || undefined,
          assignedUserId: assignedUserId || undefined,
          page,
          pageSize,
          sortBy: serverSortBy,
          sortDir: SERVER_SORT_KEYS.has(sortBy) ? sortDir : 'asc',
        });
        if (!cancelled) {
          setItems(result.data);
          setMeta(result.meta);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : t().common.error,
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [search, active, accountingBooksCategory, officeId, assignedUserId, page, pageSize, sortBy, sortDir]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  }

  function toggleSort(key: string) {
    const next = new URLSearchParams(searchParams);
    if (sort.key === key) {
      next.set('sortDir', sort.direction === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sortBy', key);
      next.set('sortDir', 'asc');
    }
    next.delete('page');
    setSearchParams(next);
  }

  const displayItems = useMemo(() => {
    if (SERVER_SORT_KEYS.has(sort.key)) return items;
    return sortItems(items, sort, customerSortGetters);
  }, [items, sort]);

  const hasFilters = Boolean(
    search || active || accountingBooksCategory || officeId || assignedUserId,
  );

  function clearFilters() {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  }

  async function handleExport(format: ListExportFormat) {
    setExporting(true);
    setError(null);
    try {
      const serverSort = SERVER_SORT_KEYS.has(sort.key);
      const rows = await collectPages((nextPage) =>
        api.getCustomers({
          search: search || undefined,
          active: active || undefined,
          accountingBooksCategory: accountingBooksCategory || undefined,
          officeId: officeId || undefined,
          assignedUserId: assignedUserId || undefined,
          page: nextPage,
          pageSize: 100,
          sortBy: serverSort ? sort.key : 'name',
          sortDir: serverSort ? sort.direction : 'asc',
        }),
      );
      const exported = serverSort
        ? rows
        : sortItems(rows, sort, customerSortGetters);
      const allLabel = labels.filterAll;
      exportTabularList({
        filename: 'pelates.xls',
        title: labels.title,
        meta: [
          {
            label: t().common.search,
            value: search || '—',
          },
          {
            label: labels.status,
            value:
              active === 'true'
                ? labels.filterActive
                : active === 'false'
                  ? labels.filterInactive
                  : allLabel,
          },
          {
            label: labels.accountingBooksCategory,
            value:
              accountingBooksCategory === 'B_SINGLE_ENTRY'
                ? labels.booksSingleEntry
                : accountingBooksCategory === 'C_DOUBLE_ENTRY'
                  ? labels.booksDoubleEntry
                  : allLabel,
          },
          {
            label: t().nav.office,
            value:
              offices.find((office) => String(office.id) === officeId)?.name ??
              allLabel,
          },
          {
            label: labels.assignedUser,
            value:
              assignees.find((user) => String(user.id) === assignedUserId)
                ?.name ?? allLabel,
          },
        ],
        headers: [
          labels.name,
          labels.vatNumber,
          labels.phone,
          labels.email,
          labels.accountingBooksCategory,
          t().nav.office,
          labels.assignedUser,
          labels.agreedAmount,
          labels.outstandingBalance,
          labels.status,
        ],
        columnWidths: [28, 14, 16, 28, 18, 18, 22, 14, 14, 14],
        numericIndexes: [7, 8],
        rows: exported.map((customer) => [
          textCell(customer.name),
          textCell(customer.vatNumber),
          textCell(customer.phone),
          textCell(customer.email),
          textCell(accountingBooksLabel(customer.accountingBooksCategory)),
          textCell(customer.office?.name),
          textCell(customer.assignedUser?.name),
          moneyCell(customer.agreedAmount.total),
          moneyCell(customer.outstandingBalance.total),
          textCell(customer.active ? labels.active : labels.inactive),
        ]),
        pdfRows: exported.map((customer) => [
          customer.name,
          customer.vatNumber || '—',
          customer.phone || '—',
          customer.email || '—',
          accountingBooksLabel(customer.accountingBooksCategory),
          customer.office?.name || '—',
          customer.assignedUser?.name || '—',
          formatMoney(customer.agreedAmount.total),
          formatMoney(customer.outstandingBalance.total),
          customer.active ? labels.active : labels.inactive,
        ]),
        moneyTotals: [
          sumMoney(exported.map((customer) => customer.agreedAmount.total)),
          sumMoney(
            exported.map((customer) => customer.outstandingBalance.total),
          ),
        ],
        format,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete(customer: CustomerListItem) {
    const ok = await confirm({
      title: labels.delete,
      message: labels.deleteConfirm.replace('{name}', customer.name),
      confirmText: labels.delete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deleteCustomer(customer.id);
      setItems((prev) => prev.filter((item) => item.id !== customer.id));
      setMeta((prev) => ({
        ...prev,
        total: Math.max(0, prev.total - 1),
        totals: {
          agreedAmount: sumMoney([
            prev.totals.agreedAmount,
            `-${customer.agreedAmount.total}`,
          ]),
          outstandingBalance: sumMoney([
            prev.totals.outstandingBalance,
            `-${customer.outstandingBalance.total}`,
          ]),
        },
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    }
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <>
            <ExportIconButtons
              disabled={loading || exporting || meta.total === 0}
              onExcel={() => void handleExport('excel')}
              onPdf={() => void handleExport('pdf')}
            />
            {hasFilters ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearFilters}
              >
                <XIcon className="h-3.5 w-3.5" />
                {t().common.clearFilters}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => updateParam('search', searchInput)}
            >
              <SearchIcon className="h-3.5 w-3.5" />
              {t().common.search}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => navigate('/customers/new')}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {labels.new}
            </Button>
          </>
        }
      >
        <FilterField wide label={t().common.search}>
          <TextInput
            type="search"
            value={searchInput}
            showClearButton={Boolean(searchInput)}
            size="sm"
            placeholder={labels.searchPlaceholder}
            prefix={<SearchIcon className="h-4 w-4" />}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateParam('search', searchInput);
              }
            }}
          />
        </FilterField>
        <FilterField label={labels.status}>
          <Select
            value={active}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: labels.filterAll },
              { value: 'true', label: labels.filterActive },
              { value: 'false', label: labels.filterInactive },
            ]}
            onChange={(e) => updateParam('active', e.target.value)}
          />
        </FilterField>
        <FilterField wide label={labels.accountingBooksCategory}>
          <Select
            value={accountingBooksCategory}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: labels.filterAll },
              { value: 'B_SINGLE_ENTRY', label: labels.booksSingleEntry },
              { value: 'C_DOUBLE_ENTRY', label: labels.booksDoubleEntry },
            ]}
            onChange={(e) =>
              updateParam('accountingBooksCategory', e.target.value)
            }
          />
        </FilterField>
        <FilterField label={t().nav.office}>
          <Select
            value={officeId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: labels.filterAll },
              ...offices.map((office) => ({
                value: String(office.id),
                label: office.active
                  ? office.name
                  : `${office.name} (${labels.inactive})`,
              })),
            ]}
            onChange={(e) => updateParam('officeId', e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.assignedUser}>
          <Select
            value={assignedUserId}
            size="sm"
            showClearButton={false}
            options={[
              { value: '', label: labels.filterAll },
              ...assignees.map((user) => ({
                value: String(user.id),
                label: user.active
                  ? user.name
                  : `${user.name} (${labels.inactive})`,
              })),
            ]}
            onChange={(e) => updateParam('assignedUserId', e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel
        footer={
          !loading && meta.total > 0 ? (
            <ListPagination
              page={meta.page}
              totalPages={meta.pageCount}
              total={meta.total}
              itemsPerPage={meta.pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={(nextPage) =>
                updateParam('page', String(nextPage))
              }
              onItemsPerPageChange={(size) =>
                updateParam('pageSize', String(size))
              }
            />
          ) : null
        }
      >
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : displayItems.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {t().common.actions}
                </th>
                <SortableTh sortKey="name" sort={sort} onSort={toggleSort}>
                  {labels.name}
                </SortableTh>
                <SortableTh sortKey="vatNumber" sort={sort} onSort={toggleSort}>
                  {labels.vatNumber}
                </SortableTh>
                <SortableTh sortKey="phone" sort={sort} onSort={toggleSort}>
                  {labels.phone}
                </SortableTh>
                <SortableTh sortKey="email" sort={sort} onSort={toggleSort}>
                  {labels.email}
                </SortableTh>
                <SortableTh
                  sortKey="accountingBooksCategory"
                  sort={sort}
                  onSort={toggleSort}
                  wrap
                  className="w-40"
                >
                  {labels.accountingBooksCategory}
                </SortableTh>
                <SortableTh
                  sortKey="office"
                  sort={sort}
                  onSort={toggleSort}
                  className="w-40"
                >
                  {t().nav.office}
                </SortableTh>
                <SortableTh
                  sortKey="assignedUser"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.assignedUser}
                </SortableTh>
                <SortableTh
                  sortKey="agreedAmount"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.agreedAmount}
                </SortableTh>
                <SortableTh
                  sortKey="outstandingBalance"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.outstandingBalance}
                </SortableTh>
                <SortableTh sortKey="active" sort={sort} onSort={toggleSort}>
                  {labels.status}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((customer) => (
                <tr key={customer.id} className="border-t border-line">
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="inline-flex items-center gap-0.5">
                      <TableIconLink
                        to={`/customers/${customer.id}`}
                        label={labels.view}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </TableIconLink>
                      <TableIconLink
                        to={`/customers/${customer.id}/edit`}
                        label={labels.editAction}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </TableIconLink>
                      {isAdmin ? (
                        <TableIconButton
                          label={labels.delete}
                          variant="danger"
                          onClick={() => void handleDelete(customer)}
                        >
                          <TrashIcon className="h-4 w-4" />
                        </TableIconButton>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2 font-medium">
                    <Link
                      to={`/customers/${customer.id}`}
                      className="text-brand hover:underline"
                    >
                      {customer.name}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{customer.vatNumber || '—'}</td>
                  <td className="px-3 py-2">{customer.phone || '—'}</td>
                  <td className="px-3 py-2">{customer.email || '—'}</td>
                  <td className="w-40 px-3 py-2">
                    {accountingBooksLabel(customer.accountingBooksCategory)}
                  </td>
                  <td className="w-40 px-3 py-2">{customer.office?.name || '—'}</td>
                  <td className="px-3 py-2">
                    {customer.assignedUser?.name || '—'}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(customer.agreedAmount.total)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(customer.outstandingBalance.total)}
                  </td>
                  <td className="px-3 py-2">
                    <Badge
                      variant={customer.active ? 'success' : 'secondary'}
                    >
                      {customer.active ? labels.active : labels.inactive}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={dataTableFootClass}>
              <tr>
                <td className="px-3 py-2" colSpan={8}>
                  {`${t().common.total} · ${(meta.total === 1
                    ? labels.listCustomerCountOne
                    : labels.listCustomerCount
                  ).replace('{count}', String(meta.total))}`}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.agreedAmount)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.outstandingBalance)}
                </td>
                <td className="px-3 py-2" />
              </tr>
            </tfoot>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
